// A situation is an alarm plus the assistance requests (V4 alarms of their own) it started.
// Duck-typed on the Alarm Record so it imports no model and tests without mocks.

export const V4_ALARM_TYPE = {
    ASSISTANCE_REQUEST: 'assistance_request',
};

// SDK `AlarmReceiverResponseStatus`, which only names them Value0..Value4.
export const V4_RECEIVER_STATUS = {
    SENDING: 0,
    PENDING: 1,
    ACCEPTED: 2,
    REJECTED: 3,
    FAILED: 4,
};

// Only message, delay_seconds and source are live; the rest is a pending backend contract.
// Until it ships every request is its own situation, with no "needed" count or follow-up window.
export const ASSISTANCE_REQUEST_META = {
    MESSAGE: 'message',
    DELAY_SECONDS: 'delay_seconds',
    SOURCE: 'source',
    NAME: 'name',
    MIN_TURNOUT: 'min_turnout',
    SOURCE_ALARM_ID: 'source_alarm_id',
    FOLLOW_UP_OF: 'follow_up_of',
    // Present-but-empty means "no follow-up"; absent means unknown, which shows no window.
    FOLLOW_UP_SCENARIO_ID: 'follow_up_scenario_id',
};

export const LARGE_REQUEST_RECIPIENTS = 20;

const idOf = (alarm) => String(alarm?.get?.('id') ?? '');

const toArray = (alarms) => {
    if (!alarms) return [];
    if (Array.isArray(alarms)) return alarms;
    return typeof alarms.toArray === 'function' ? alarms.toArray() : [];
};

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');

export const metadataLinkId = (value) =>
    (typeof value === 'string' || typeof value === 'number') && String(value).trim()
        ? String(value).trim()
        : '';

const positiveInt = (value) => {
    const n = Math.floor(Number(value));
    return Number.isFinite(n) && n > 0 ? n : null;
};

// V4 zero timestamps read `0001-01-01T…`.
const toUnix = (ts) => {
    if (!ts || String(ts).startsWith('0001-')) return 0;
    const ms = new Date(ts).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : 0;
};

const metadataOf = (alarm) => alarm?.get?.('v4Data')?.metadata || {};

export const isAssistanceRequestAlarm = (alarm) =>
    !!alarm?.get?.('v4') && alarm.get('v4Type') === V4_ALARM_TYPE.ASSISTANCE_REQUEST;

export function receiverGroups(receivers) {
    const groups = {accepted: [], rejected: [], waiting: [], failed: []};
    (Array.isArray(receivers) ? receivers : []).forEach((r) => {
        if (!r || toUnix(r.deleted_at) || !r.device_id) return;
        const entry = {
            deviceId: String(r.device_id),
            // A receiver keeps only its latest state, so its update is the answer time.
            at: toUnix(r.updated_at) || toUnix(r.created_at),
            teamIds: Array.isArray(r.team_ids) ? r.team_ids : [],
            geofenceIds: Array.isArray(r.geofence_ids) ? r.geofence_ids : [],
            reason: r.response?.reason || '',
        };
        switch (Number(r.response?.status)) {
            case V4_RECEIVER_STATUS.ACCEPTED:
                groups.accepted.push(entry);
                break;
            case V4_RECEIVER_STATUS.REJECTED:
                groups.rejected.push(entry);
                break;
            case V4_RECEIVER_STATUS.FAILED:
                groups.failed.push(entry);
                break;
            default:
                groups.waiting.push(entry);
        }
    });
    const byTime = (a, b) => a.at - b.at;
    groups.accepted.sort(byTime);
    groups.rejected.sort(byTime);
    return {
        ...groups,
        total: groups.accepted.length + groups.rejected.length + groups.waiting.length + groups.failed.length,
    };
}

export function assistanceRequestOf(alarm) {
    const meta = metadataOf(alarm);
    const followUpKey = ASSISTANCE_REQUEST_META.FOLLOW_UP_SCENARIO_ID;
    const hasFollowUpKey = Object.prototype.hasOwnProperty.call(meta, followUpKey);
    const isOpen = !!alarm.isOpen;
    return {
        id: idOf(alarm),
        alarm,
        name: trimmed(meta[ASSISTANCE_REQUEST_META.NAME]),
        message: trimmed(meta[ASSISTANCE_REQUEST_META.MESSAGE]),
        needed: positiveInt(meta[ASSISTANCE_REQUEST_META.MIN_TURNOUT]),
        delaySeconds: positiveInt(meta[ASSISTANCE_REQUEST_META.DELAY_SECONDS]),
        followUpConfigured: hasFollowUpKey ? !!metadataLinkId(meta[followUpKey]) : null,
        followUpOf: alarm.get('followUpOfId') || '',
        sentAt: alarm.get('date') || 0,
        isOpen,
        // A closed V4 alarm keeps its close time in `checked`.
        closedAt: isOpen ? 0 : alarm.get('checked') || 0,
        receivers: receiverGroups(alarm.get('v4Data')?.receivers),
    };
}

// Counts the whole follow-up chain: whoever accepted an earlier request is still coming.
export function chainAcceptances(situation, request) {
    if (!request) return [];
    if (!situation) return request.receivers.accepted;
    const requestsById = new Map((situation.requests || []).map((r) => [r.id, r]));
    const firstByDevice = new Map();
    const seen = new Set();
    for (let at = request; at && !seen.has(at.id); at = requestsById.get(at.followUpOf)) {
        seen.add(at.id);
        at.receivers.accepted.forEach((r) => {
            const known = firstByDevice.get(r.deviceId);
            if (!known || r.at < known.at) firstByDevice.set(r.deviceId, r);
        });
    }
    return [...firstByDevice.values()].sort((a, b) => a.at - b.at);
}

export function targetReachedAt(request, situation) {
    const needed = request?.needed;
    const accepted = chainAcceptances(situation, request);
    if (!needed || accepted.length < needed) return 0;
    return accepted[needed - 1].at || 0;
}

// Ties break on id, so the pick does not depend on list order.
const olderFirst = (a, b) => {
    const byDate = (a.get('date') || 0) - (b.get('date') || 0);
    if (byDate) return byDate;
    const [idA, idB] = [idOf(a), idOf(b)];
    if (idA === idB) return 0;
    return idA < idB ? -1 : 1;
};

// A request joins its parent only while the parent is held: it is never hidden behind an
// alarm the list does not show.
export function groupAlarmSituations(alarms) {
    const byId = new Map();
    toArray(alarms).forEach((a) => {
        if (a) byId.set(idOf(a), a);
    });
    const list = [...byId.values()];

    const parentOf = (alarm) => {
        if (!isAssistanceRequestAlarm(alarm)) return null;
        const id = idOf(alarm);
        const linkId = [alarm.get('followUpOfId'), alarm.get('sourceAlarmId')]
            .find((link) => link && link !== id && byId.has(link));
        return linkId ? byId.get(linkId) : null;
    };

    // A cycle (a data error) roots at its oldest member, wherever the walk started.
    const roots = new Map();
    const rootOf = (alarm) => {
        const path = [];
        const onPath = new Map();
        let current = alarm;
        let root = null;
        while (!root) {
            const id = idOf(current);
            if (roots.has(id)) {
                root = roots.get(id);
            } else if (onPath.has(id)) {
                root = path.slice(onPath.get(id)).sort(olderFirst)[0];
            } else {
                onPath.set(id, path.length);
                path.push(current);
                const parent = parentOf(current);
                if (parent) current = parent;
                else root = current;
            }
        }
        path.forEach((a) => roots.set(idOf(a), root));
        return root;
    };

    const members = new Map();
    list.forEach((alarm) => {
        const rootId = idOf(rootOf(alarm));
        if (!members.has(rootId)) members.set(rootId, []);
        members.get(rootId).push(alarm);
    });

    const situations = [];
    list.forEach((alarm) => {
        const id = idOf(alarm);
        if (!members.has(id)) return;
        const group = members.get(id);
        const memberIds = group.map(idOf);
        // Drop self-links and the root's link back into its cycle, so follow-up chains stay finite.
        const brokenLink = (r) =>
            r.followUpOf === r.id || (r.id === id && memberIds.includes(r.followUpOf));
        const requests = group
            .filter(isAssistanceRequestAlarm)
            .map(assistanceRequestOf)
            .map((r) => (r.followUpOf && brokenLink(r) ? {...r, followUpOf: ''} : r))
            .sort((a, b) => a.sentAt - b.sentAt);
        situations.push({
            key: alarm.key,
            root: alarm,
            alarm: isAssistanceRequestAlarm(alarm) ? null : alarm,
            requests,
            memberIds,
            isOpen: !!alarm.isOpen || requests.some((r) => r.isOpen),
            date: alarm.get('date') || 0,
        });
    });
    return situations.sort((a, b) => b.date - a.date);
}

export function situationOf(situations, alarmId) {
    if (alarmId == null || alarmId === '') return null;
    const id = String(alarmId);
    return (situations || []).find((s) => s.memberIds.includes(id)) || null;
}

export function latestRequestsOf(situation) {
    const requests = situation?.requests || [];
    const followed = new Set(requests.map((r) => r.followUpOf).filter(Boolean));
    return requests.filter((r) => !followed.has(r.id));
}

export const followUpsOf = (situation, request) =>
    (situation?.requests || []).filter((r) => r.followUpOf && r.followUpOf === request.id);

export function requestsInDisplayOrder(situation) {
    const requests = [...(situation?.requests || [])].sort((a, b) => a.sentAt - b.sentAt);
    const ids = new Set(requests.map((r) => r.id));
    const out = [];
    const seen = new Set();
    // An explicit stack, so a long chain cannot overflow the call stack.
    const visit = (start) => {
        const stack = [start];
        while (stack.length) {
            const request = stack.pop();
            if (!seen.has(request.id)) {
                seen.add(request.id);
                out.push(request);
                const followUps = requests.filter((r) => r.followUpOf === request.id && !seen.has(r.id));
                for (let i = followUps.length - 1; i >= 0; i -= 1) stack.push(followUps[i]);
            }
        }
    };
    requests
        .filter((r) => !r.followUpOf || r.followUpOf === r.id || !ids.has(r.followUpOf))
        .forEach(visit);
    // A follow-up loop (a data error) has no first request: start it at its oldest.
    requests.forEach(visit);
    return out;
}

export function openRequestsClosedWith(closing, allAlarms) {
    const closingIds = new Set(toArray(closing).map(idOf));
    if (!closingIds.size) return [];
    const out = [];
    groupAlarmSituations(allAlarms).forEach((situation) => {
        if (!closingIds.has(idOf(situation.root))) return;
        situation.requests.forEach((r) => {
            if (r.isOpen && !closingIds.has(r.id)) out.push(r.alarm);
        });
    });
    return out;
}

export const RESPONSE_STATUS = {
    ARC_ONLY: 'arcOnly',
    NONE: 'none',
    NO_RECIPIENTS: 'noRecipients',
    NONE_ACCEPTED: 'noneAccepted',
    PARTIAL: 'partial',
    REACHED: 'reached',
    ACCEPTED: 'accepted',
    RESPONDED: 'responded',
};

// Only a delivered forward counts: one still on its way may yet fail.
const arcNotified = (situation) => situation?.alarm?.arcForwardState === 'success';

// A follow-up chain is one call-out: each device counts once however often it was asked.
const chainStats = (requestsById, latest) => {
    const asked = new Set();
    const accepted = new Set();
    const answered = new Set();
    const seen = new Set();
    for (let at = latest; at && !seen.has(at.id); at = requestsById.get(at.followUpOf)) {
        seen.add(at.id);
        const {receivers} = at;
        [...receivers.accepted, ...receivers.rejected, ...receivers.waiting, ...receivers.failed]
            .forEach((r) => asked.add(r.deviceId));
        receivers.accepted.forEach((r) => {
            accepted.add(r.deviceId);
            answered.add(r.deviceId);
        });
        receivers.rejected.forEach((r) => answered.add(r.deviceId));
    }
    return {accepted: accepted.size, responded: answered.size, total: asked.size};
};

export function situationResponse(situation) {
    const requests = situation?.requests || [];
    const base = {
        requestCount: requests.length,
        arcNotified: arcNotified(situation),
        accepted: 0,
        needed: 0,
        responded: 0,
        total: 0,
    };
    if (!requests.length) {
        return {...base, kind: base.arcNotified ? RESPONSE_STATUS.ARC_ONLY : RESPONSE_STATUS.NONE};
    }

    const requestsById = new Map(requests.map((r) => [r.id, r]));
    let accepted = 0;
    let neededAccepted = 0;
    let needed = 0;
    let responded = 0;
    let total = 0;
    let allNeededKnown = true;
    let allReached = true;
    latestRequestsOf(situation).forEach((r) => {
        const chain = chainStats(requestsById, r);
        const count = chain.accepted;
        accepted += count;
        responded += chain.responded;
        total += chain.total;
        if (r.needed) {
            needed += r.needed;
            neededAccepted += Math.min(count, r.needed);
            if (count < r.needed) allReached = false;
        } else {
            allNeededKnown = false;
        }
    });

    const counts = {...base, accepted, responded, total, needed};
    // A follow-up that reached nobody does not undo an acceptance earlier in its chain.
    if (!total && !accepted) return {...counts, kind: RESPONSE_STATUS.NO_RECIPIENTS};
    if (allNeededKnown) {
        if (allReached) return {...counts, accepted: neededAccepted, kind: RESPONSE_STATUS.REACHED};
        if (!neededAccepted) return {...counts, accepted: 0, kind: RESPONSE_STATUS.NONE_ACCEPTED};
        return {...counts, accepted: neededAccepted, kind: RESPONSE_STATUS.PARTIAL};
    }
    if (total > LARGE_REQUEST_RECIPIENTS) return {...counts, kind: RESPONSE_STATUS.RESPONDED};
    if (!accepted) return {...counts, kind: RESPONSE_STATUS.NONE_ACCEPTED};
    return {...counts, kind: RESPONSE_STATUS.ACCEPTED};
}

export const FOLLOW_UP_STATE = {
    COUNTING: 'counting',
    SENT: 'sent',
    ENDED: 'ended',
};

// `now` is unix seconds.
export function followUpStateOf(situation, request, now) {
    if (!request?.delaySeconds) return null;
    const due = request.sentAt + request.delaySeconds;
    const followUp = followUpsOf(situation, request)[0] || null;
    if (followUp) return {kind: FOLLOW_UP_STATE.SENT, due, followUp};
    if (request.followUpConfigured !== true) return null;
    const reachedAt = targetReachedAt(request, situation);
    if (reachedAt && reachedAt <= due) return null;
    if (!request.isOpen && request.closedAt && request.closedAt <= due) return null;
    if (request.isOpen && now < due) {
        return {kind: FOLLOW_UP_STATE.COUNTING, due, secondsLeft: due - now};
    }
    return {kind: FOLLOW_UP_STATE.ENDED, due};
}

export const TIMELINE = {
    ALARM_RAISED: 'alarmRaised',
    LOCATION_FIXED: 'locationFixed',
    ARC_NOTIFIED: 'arcNotified',
    ARC_FAILED: 'arcFailed',
    REQUEST_SENT: 'requestSent',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
    TARGET_REACHED: 'targetReached',
    REQUEST_CLOSED: 'requestClosed',
    ALARM_CLOSED: 'alarmClosed',
};

const listsAnswers = (request) => request.receivers.total <= LARGE_REQUEST_RECIPIENTS;

export function situationTimeline(situation) {
    if (!situation) return [];
    const entries = [];
    const add = (at, kind, extra) => {
        if (at) entries.push({at, kind, ...extra});
    };

    const alarm = situation.alarm;
    if (alarm) {
        add(alarm.get('date'), TIMELINE.ALARM_RAISED, {alarm});
        const location = alarm.get('v4Data')?.location;
        add(toUnix(location?.determined_at), TIMELINE.LOCATION_FIXED, {
            address: location?.geocoded?.formatted || location?.geocoded?.address || '',
        });
        const forward = alarm.get('arcForward');
        const forwardState = alarm.arcForwardState;
        if (forwardState === 'success') {
            add(toUnix(forward?.forwarded_at) || alarm.get('date'), TIMELINE.ARC_NOTIFIED);
        } else if (forwardState === 'failed') {
            add(toUnix(forward?.forwarded_at) || alarm.get('date'), TIMELINE.ARC_FAILED);
        }
    }

    situation.requests.forEach((request) => {
        add(request.sentAt, TIMELINE.REQUEST_SENT, {request, followUp: !!request.followUpOf});
        if (listsAnswers(request)) {
            request.receivers.accepted.forEach((r) =>
                add(r.at, TIMELINE.ACCEPTED, {request, deviceId: r.deviceId}));
            request.receivers.rejected.forEach((r) =>
                add(r.at, TIMELINE.REJECTED, {request, deviceId: r.deviceId}));
        }
        add(targetReachedAt(request, situation), TIMELINE.TARGET_REACHED, {request});
        // Closed within seconds of its alarm: part of that close, not an entry of its own.
        const closedWithAlarm = alarm && !alarm.isOpen && Math.abs((alarm.get('checked') || 0) - request.closedAt) <= 5;
        if (!closedWithAlarm) {
            add(request.closedAt, TIMELINE.REQUEST_CLOSED, {request, expired: request.alarm.stateName === 'expired'});
        }
    });

    if (alarm && !alarm.isOpen) {
        add(alarm.get('checked'), TIMELINE.ALARM_CLOSED, {
            alarm,
            notes: alarm.get('notes') || '',
            expired: alarm.stateName === 'expired',
        });
    }

    // Same-second entries keep insertion order (cause before effect).
    return entries
        .map((entry, index) => ({entry, index}))
        .sort((a, b) => a.entry.at - b.entry.at || a.index - b.index)
        .map(({entry}) => entry);
}
